import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Paper,
  Divider,
  Avatar,
  Stack,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Book,
  BarChart,
  EmojiEvents,
  AccessTime,
  CheckCircle,
  TrendingUp,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import StudentSidebar from "./StudentSdebar";

const Report = () => {
  const theme = useTheme();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [loading, setLoading] = useState(false);

  // Mock Data (matches original)
  const studentData = {
    name: "Rajesh Kumar Sharma",
    id: "STU2023001",
    email: "rajesh.sharma@email.com",
    mobile: "+91-9876543210",
    enrollmentDate: "2024-01-10",
    lastActive: "2024-06-20",
    courses: [
      {
        name: "Organic Farming",
        enrolledDate: "2024-01-15",
        progress: 85,
        grade: "A",
        certificate: "Yes",
      },
      {
        name: "Soil Management",
        enrolledDate: "2024-03-05",
        progress: 65,
        grade: "B+",
        certificate: "In Progress",
      },
    ],
    achievements: [
      "Gold Medal in Organic Farming",
      "Top Performer (March 2024)",
    ],
    stats: {
      totalCourses: 2,
      completedCourses: 0,
      avgProgress: 75,
      certificatesEarned: 1,
    },
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Header />
      
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? 'calc(100% - 280px)' : 'calc(100% - 80px)',
            transition: theme.transitions.create('width', { duration: 300 }),
            height: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={60} thickness={4} sx={{ color: '#D4AF37' }} />
            </Box>
          ) : (
            <Container maxWidth="lg">
              <Box sx={{ mb: 4 }}>
                <Typography variant="h3" fontWeight={800} gutterBottom>Learning Report</Typography>
                <Typography variant="body1" color="text.secondary">Detailed analysis of your academic performance and milestones.</Typography>
              </Box>

              {/* Stats Overview */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                  { title: "Total Courses", value: studentData.stats.totalCourses, icon: <Book />, color: '#D4AF37' },
                  { title: "Avg Progress", value: `${studentData.stats.avgProgress}%`, icon: <BarChart />, color: '#4caf50' },
                  { title: "Certificates", value: studentData.stats.certificatesEarned, icon: <EmojiEvents />, color: '#2196f3' },
                  { title: "Last Active", value: formatDate(studentData.lastActive), icon: <AccessTime />, color: '#ff9800' },
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card sx={{ 
                      borderRadius: '16px', 
                      border: '1px solid', 
                      borderColor: 'divider',
                      transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-5px)', borderColor: '#D4AF37' }
                    }}>
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Avatar sx={{ bgcolor: `${item.color}20`, color: item.color, mx: 'auto', mb: 2 }}>
                          {item.icon}
                        </Avatar>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>{item.title}</Typography>
                        <Typography variant="h5" fontWeight={800}>{item.value}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={4}>
                {/* Courses Progress */}
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 4, borderRadius: '20px', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h5" fontWeight={800} gutterBottom sx={{ mb: 3 }}>My Academic Progress</Typography>
                    <Stack spacing={4}>
                      {studentData.courses.map((course, idx) => (
                        <Box key={idx}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
                            <Box>
                              <Typography variant="h6" fontWeight={700}>{course.name}</Typography>
                              <Typography variant="caption" color="text.secondary">Enrolled on: {formatDate(course.enrolledDate)}</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={800} color="primary.main">{course.progress}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={course.progress} 
                            sx={{ 
                              height: 10, 
                              borderRadius: 5, 
                              bgcolor: 'rgba(212, 175, 55, 0.1)',
                              '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37' }
                            }} 
                          />
                          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                            <Chip label={`Grade: ${course.grade}`} size="small" color="success" sx={{ fontWeight: 600 }} />
                            <Chip 
                              label={`Certificate: ${course.certificate}`} 
                              size="small" 
                              variant="outlined" 
                              color={course.certificate === 'Yes' ? 'primary' : 'warning'} 
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>

                {/* Achievements */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 4, borderRadius: '20px', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <EmojiEvents />
                      <Typography variant="h5" fontWeight={800}>Achievements</Typography>
                    </Box>
                    <Stack spacing={2}>
                      {studentData.achievements.map((ach, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            p: 2, 
                            bgcolor: 'rgba(255,255,255,0.1)', 
                            borderRadius: '12px' 
                          }}
                        >
                          <CheckCircle sx={{ fontSize: '1rem' }} />
                          <Typography variant="body2" fontWeight={600}>{ach}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                  
                  <Card sx={{ mt: 3, borderRadius: '20px', border: '1px solid', borderColor: '#D4AF37', bgcolor: 'rgba(212, 175, 55, 0.05)' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <TrendingUp sx={{ color: '#D4AF37', fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" fontWeight={800}>Keep it up!</Typography>
                      <Typography variant="body2" color="text.secondary">You are in the top 10% of learners this month.</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Report;
